import { getCookie, removeCookie } from "@insite/client-framework/Common/Cookies";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import { ApiHandler, replaceInChain } from "@insite/client-framework/HandlerCreator";
import { getOrderApproval } from "@insite/client-framework/Services/OrderApprovalService";
import {
    createSession,
    deleteSession,
    FulfillmentMethod,
    Session,
    stopWatchingForOtherTabSessionChange,
    watchForOtherTabSessionChange,
} from "@insite/client-framework/Services/SessionService";
import {
    getCurrentUserIsGuest,
    getSession,
    getSettingsCollection,
} from "@insite/client-framework/Store/Context/ContextSelectors";
import changeCustomerContext from "@insite/client-framework/Store/Context/Handlers/ChangeCustomerContext";
import loadSession from "@insite/client-framework/Store/Context/Handlers/LoadSession";
import { ShowPricingCookieName } from "@insite/client-framework/Store/Context/Handlers/SetShowPricing";
import { chain, RedirectToChangeCustomer, SignInParameter, SignInResult } from "@insite/client-framework/Store/Context/Handlers/SignIn";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { isVmiAdmin } from "@insite/client-framework/Store/Data/VmiLocations/VmiLocationsSelectors";
import { getHomePageUrl, getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import { Draft } from "immer";
import cloneDeep from "lodash/cloneDeep";

type HandlerType = ApiHandler<
    SignInParameter,
    SignInResult,
    {
        tokenSuccessfullyRequested?: boolean;
        authenticatedSession?: Session;
    }
>;

export const RedirectToChangeCustomerUpdated: HandlerType = async props => {
    if (!props.authenticatedSession?.redirectToChangeCustomerPageOnSignIn) {
        watchForOtherTabSessionChange(); // This sign-in failed, but another tab might be successful later on.
        return;
    }

    // bypass Change Customer page when user open invite link from Share List email
    const myListsDetailsPageUrl = getPageLinkByPageType(props.getState(), "MyListsDetailsPage")?.url;
    if (
        props.parameter.returnUrl &&
        myListsDetailsPageUrl &&
        props.parameter.returnUrl.toLowerCase().indexOf(myListsDetailsPageUrl.toLowerCase()) > -1 &&
        props.parameter.returnUrl.toLowerCase().indexOf("invite=") > -1
    ) {
        return;
    }
    // bypass Change Customer page when external user views orders
    const orderApprovalDetailsPageUrl = getPageLinkByPageType(props.getState(), "OrderApprovalDetailsPage")?.url;
    if (
        props.parameter.returnUrl &&
        orderApprovalDetailsPageUrl &&
        props.parameter.returnUrl.toLowerCase().indexOf(orderApprovalDetailsPageUrl.toLowerCase()) > -1 &&
        (props.parameter.returnUrl.toLowerCase().indexOf("external-approval=") > -1 ||
            props.parameter.returnUrl.toLowerCase().indexOf("external-approval-view=") > -1)
    ) {
        const parsedQuery = parseQueryString<{ cartId: string }>(props.parameter?.returnUrl?.split("?")[1] ?? "");
        const orderApproval = await getOrderApproval({
            cartId: parsedQuery.cartId,
        });

        const { billToId, shipToId, fulfillmentMethod, pickUpWarehouse, hasDefaultCustomer } = getSession(
            props.getState(),
        );

        if (
            orderApproval &&
            orderApproval.successful &&
            orderApproval.result?.billTo &&
            orderApproval.result?.shipTo &&
            (orderApproval.result.billTo.id !== billToId || orderApproval.result.shipTo.id !== shipToId)
        ) {
            props.dispatch(
                changeCustomerContext({
                    billToId: orderApproval.result.billTo.id,
                    shipToId: orderApproval.result.shipTo.id,
                    fulfillmentMethod,
                    pickUpWarehouse,
                    isDefault: hasDefaultCustomer,
                    skipRedirect: true,
                }),
            );
        }

        return;
    }

    const currentMode = getCookie("NavigationMode") || "Storefront";
    const session = getSession(props.getState());
    const settings = getSettingsCollection(props.getState());
    const isUserVmiAdmin = isVmiAdmin(settings.orderSettings, session);
    const homePageUrl = getPageLinkByPageType(props.getState(), "HomePage")?.url;
    const dashboardPageUrl = getPageLinkByPageType(props.getState(), "MyAccountPage")?.url;
    const changeCustomerPageUrl = getPageLinkByPageType(props.getState(), "ChangeCustomerPage")?.url;
    if (homePageUrl && changeCustomerPageUrl) {
        const shouldAddReturnUrl = props.parameter.returnUrl && props.parameter.returnUrl !== homePageUrl;
        const returnUrl = shouldAddReturnUrl
            ? props.parameter.returnUrl
            : props.authenticatedSession.dashboardIsHomepage
            ? dashboardPageUrl!
            : undefined;

        window.location.href = changeCustomerPageUrl + (returnUrl ? `?returnUrl=${encodeURIComponent(returnUrl)}` : "");
        return false;
    }
};
replaceInChain(chain, RedirectToChangeCustomer, RedirectToChangeCustomerUpdated);
